import { Button, Checkbox } from "@narmi/design_system";
import { useState } from "react";
import { useLocalization } from "@fluent/react";
import type Account from "byzantine/src/Account";
import styles from "./AccountFilterContent.module.scss";

type AccountFilterContentProps = {
  accounts: Account[];
  selectedIds: Set<string> | null;
  isOpen: boolean;
  onApply: (ids: Set<string> | null) => void;
};

export function AccountFilterContent({
  accounts,
  selectedIds,
  isOpen,
  onApply
}: AccountFilterContentProps) {
  const { l10n } = useLocalization();
  const [selectedAccountIds, setSelectedAccountIds] =
    useState<Set<string> | null>(selectedIds);

  // Reset selected accounts ids
  if (!isOpen && selectedAccountIds !== selectedIds) {
    setSelectedAccountIds(selectedIds);
  }

  const allChecked =
    selectedAccountIds === null || selectedAccountIds.size === accounts.length;
  const isIndeterminate = !allChecked && (selectedAccountIds?.size ?? 0) > 0;

  const isChecked = (id: string) =>
    selectedAccountIds === null || selectedAccountIds.has(id);

  const toggleAll = () => {
    // Checked -> clear everything. Unchecked or indeterminate -> select all.
    setSelectedAccountIds(allChecked ? new Set() : null);
  };

  const toggleAccount = (id: string) => {
    // Materialize the implicit "all" state before removing from it.
    const next = new Set(selectedAccountIds ?? accounts.map((a) => a.id));
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedAccountIds(next);
  };

  return (
    <div className={styles.accountFilter}>
      <h4>{l10n.getString("pfm-accounts-heading")}</h4>

      <Checkbox
        label={l10n.getString("pfm-accounts-all")}
        checked={allChecked}
        indeterminate={isIndeterminate}
        onChange={toggleAll}
      />

      <div className={styles.accountFilterDivider} />

      <div className={styles.accountFilterList}>
        {accounts.length === 0 ? (
          <p>
            {l10n.getString("pfm-accounts-empty")}
          </p>
        ) : (
          accounts.map((account) => (
            <Checkbox
              key={account.id}
              kind="condensed"
              label={account.nickname || account.name}
              checked={isChecked(account.id)}
              onChange={() => toggleAccount(account.id)}
            />
          ))
        )}
      </div>
      <div className={styles.accountFilterFooter}>
        <Button
          label={l10n.getString("pfm-accounts-apply")}
          onClick={() => onApply(selectedAccountIds)}
        />
      </div>
    </div>
  );
}

export default AccountFilterContent;
